import React, { useContext, useEffect, useState } from 'react';
import { Box, Card, CardContent, Typography, FormControl, InputLabel, Select, MenuItem, Button, Alert, Grid, TextField, Switch, FormControlLabel, Divider, IconButton } from '@mui/material';
import { MuiColorInput } from 'mui-color-input';
import LogoutIcon from '@mui/icons-material/Logout';
import CloseIcon from '@mui/icons-material/Close';
import { ThemeConfigContext } from '../../App';
import { ActiveTeamContext } from '../../context/ActiveTeamContext';
import useDataFetch, { usePostDataFetch, usePutDataFetch } from '../../utils/useDataFetch';
import { useNavigate } from 'react-router-dom';
import CachedIcon from '@mui/icons-material/Cached';

const Settings = () => {
    const navigate = useNavigate();
    const { mode, setMode, primary, setPrimary, secondary, setSecondary } = useContext(ThemeConfigContext);
    const { activeTeamId, setActiveTeamId } = useContext(ActiveTeamContext);

    // Profile & Theme
    const { data: profile, isLoading } = useDataFetch({ url: '/users/profile' });
    const { postData: saveProfile, data: saveRes, error: saveErr, isLoading: savingProfile } = usePostDataFetch();

    // Notifications
    const { data: notifSettingsObj, isLoading: loadingNotif } = useDataFetch({ url: '/notifications/settings' });
    const { putData: saveNotif, data: notifRes, error: notifErr, isLoading: savingNotif } = usePutDataFetch();

    const [localPrimary, setLocalPrimary] = useState(primary || '');
    const [localSecondary, setLocalSecondary] = useState(secondary || '');
    const [notifState, setNotifState] = useState({
        generell_aktiv: true,
        typ_termine: true,
        typ_aufstellung: true,
        typ_kommentar: true,
        typ_umfrage: true,
        typ_strafen: true,
        typ_kasse: true,
        frist_erinnerung_stunden: 3,
        frist_erinnerung_minuten: 0
    });

    useEffect(() => {
        if (profile?.user?.theme_primary) setLocalPrimary(profile.user.theme_primary);
        if (profile?.user?.theme_secondary) setLocalSecondary(profile.user.theme_secondary);

        // Auto-select team:
        // 1. If only one team exists, force it to be the active one.
        // 2. If multiple teams exist but none are selected (or selection is invalid), pick the first one.
        if (profile?.teams && profile.teams.length > 0) {
            const hasOneTeam = profile.teams.length === 1;
            const firstId = profile.teams[0]?.team_id?.toString();
            const currentIsValid = profile.teams.some(t => t.team_id?.toString() === activeTeamId?.toString());

            if (hasOneTeam && activeTeamId?.toString() !== firstId) {
                setActiveTeamId(firstId);
            } else if (!activeTeamId || !currentIsValid) {
                setActiveTeamId(firstId);
            }
        }
    }, [profile, activeTeamId, setActiveTeamId]);

    useEffect(() => {
        if (notifSettingsObj?.settings) {
            setNotifState({
                generell_aktiv: !!notifSettingsObj.settings.generell_aktiv,
                typ_termine: !!notifSettingsObj.settings.typ_termine,
                typ_aufstellung: !!notifSettingsObj.settings.typ_aufstellung,
                typ_kommentar: !!notifSettingsObj.settings.typ_kommentar,
                typ_umfrage: !!notifSettingsObj.settings.typ_umfrage,
                typ_strafen: !!notifSettingsObj.settings.typ_strafen,
                typ_kasse: !!notifSettingsObj.settings.typ_kasse,
                frist_erinnerung_stunden: notifSettingsObj.settings.frist_erinnerung_stunden || 3,
                frist_erinnerung_minuten: notifSettingsObj.settings.frist_erinnerung_minuten || 0
            });
        }
    }, [notifSettingsObj]);

    const handleSaveTheme = async () => {
        setPrimary(localPrimary);
        setSecondary(localSecondary);
        await saveProfile({
            url: '/users/profile',
            aBody: { theme_primary: localPrimary, theme_secondary: localSecondary }
        });
    };

    const handleSaveNotifications = async () => {
        await saveNotif({
            url: '/notifications/settings',
            aBody: notifState
        });
    };

    const handleActiveTeamSelect = (value) => {
        if (!value || value === activeTeamId) return;
        const confirmed = window.confirm('Aktives Team wechseln? Die Seite wird neu geladen, damit team-spezifische Rollen und Berechtigungen korrekt geladen werden.');
        if (confirmed) {
            setActiveTeamId(value);
            window.location.reload();
        }
    };

    const clearCache = async () => {
        try {
            // 1. Alle Cache-Speicher (Cache Storage) löschen
            if ('caches' in window) {
                const cacheNames = await caches.keys();
                await Promise.all(
                    cacheNames.map((cacheName) => caches.delete(cacheName))
                );
                console.log('Cache Storage erfolgreich geleert.');
            }

            // 2. Registrierte Service Worker finden und abmelden
            if ('serviceWorker' in navigator) {
                const registrations = await navigator.serviceWorker.getRegistrations();
                for (let registration of registrations) {
                    await registration.unregister();
                }
                console.log('Service Worker erfolgreich deregistriert.');
            }

            // 3. Die Seite komplett neu laden (erzwingt das Laden vom Server)
            window.location.reload();
        } catch (error) {
            console.error('Fehler beim Leeren des Caches:', error);
            // Fallback: Trotzdem neu laden, falls etwas fehlschlägt
            window.location.reload();
        }
    };

    const handleLogout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        navigate('/auth');
    };

    if (isLoading || loadingNotif) return <Box sx={{ p: 3 }}><Typography>Lade Einstellungen...</Typography></Box>;

    return (
        <Box sx={{ p: { xs: 2, md: 5 }, maxWidth: 800, mx: 'auto', display: 'flex', flexDirection: 'column', gap: 3 }}>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h4">Einstellungen</Typography>
            </Box>

            <Grid container spacing={3}>
                {/* 1. Aktives Team */}
                <Grid size={{ xs: 12 }}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>Aktives Team wählen</Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                Wähle das Team aus, in dem du gerade interagieren möchtest (z.B. für Termine oder Mannschaftskasse).
                            </Typography>
                            {profile?.teams?.length > 0 ? (
                                <FormControl fullWidth>
                                    <InputLabel>Aktives Team</InputLabel>
                                    <Select
                                        value={activeTeamId || ''}
                                        label="Aktives Team"
                                        onChange={(e) => handleActiveTeamSelect(e.target.value)}
                                    >
                                        {profile.teams.map((t, idx) => (
                                            <MenuItem key={t.team_id || idx} value={t.team_id?.toString() || ""}>
                                                {t.team_name} (Rolle: {t.rolle})
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            ) : (
                                <Alert severity="warning">Du bist noch in keinem Team. Tritt einem bei oder gründe eins!</Alert>
                            )}
                        </CardContent>
                    </Card>
                </Grid>

                {/* 2. Theme Settings */}
                <Grid size={{ xs: 12 }}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>Personalisiertes Design</Typography>

                            <FormControl fullWidth sx={{ mb: 3 }}>
                                <InputLabel>Modus</InputLabel>
                                <Select value={mode} label="Modus" onChange={(e) => setMode(e.target.value)}>
                                    <MenuItem value="light">Hell</MenuItem>
                                    <MenuItem value="dark">Dunkel</MenuItem>
                                </Select>
                            </FormControl>

                            {/* <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                                <MuiColorInput 
                                    format="hex"
                                    label="Hauptfarbe (Primary Hex)" 
                                    value={localPrimary || '#6366f1'} 
                                    onChange={(color) => setLocalPrimary(color)}
                                />
                                <MuiColorInput 
                                    format="hex"
                                    label="Akzentfarbe (Secondary Hex)" 
                                    value={localSecondary || '#ec4899'} 
                                    onChange={(color) => setLocalSecondary(color)}
                                />
                            </Box> */}

                            <Button variant="contained" onClick={handleSaveTheme} disabled={savingProfile}>
                                Design speichern
                            </Button>

                            {saveRes?.success && <Alert severity="success" sx={{ mt: 2 }}>Erfolgreich gespeichert!</Alert>}
                            {saveErr && <Alert severity="error" sx={{ mt: 2 }}>{saveErr}</Alert>}
                        </CardContent>
                    </Card>
                </Grid>

                {/* 3. Push Benachrichtigungen */}
                <Grid size={{ xs: 12 }}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>Push-Benachrichtigungen</Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                Definiere, worüber du aktiv benachrichtigt werden möchtest.
                            </Typography>

                            <FormControlLabel
                                control={<Switch checked={notifState.generell_aktiv} onChange={(e) => setNotifState({ ...notifState, generell_aktiv: e.target.checked })} color="primary" />}
                                label="Benachrichtigungen generell aktivieren"
                                sx={{ mb: 2, display: 'block' }}
                            />

                            <Divider sx={{ mb: 2 }} />

                            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold' }}>Frist-Erinnerungen</Typography>
                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
                                <TextField
                                    type="number"
                                    label="Stunden"
                                    value={notifState.frist_erinnerung_stunden}
                                    onChange={(e) => setNotifState({ ...notifState, frist_erinnerung_stunden: parseInt(e.target.value) || 0 })}
                                    inputProps={{ min: 0, max: 23 }}
                                    sx={{ width: 100 }}
                                    disabled={!notifState.generell_aktiv}
                                />
                                <Typography variant="body2">:</Typography>
                                <TextField
                                    type="number"
                                    label="Minuten"
                                    value={notifState.frist_erinnerung_minuten}
                                    onChange={(e) => setNotifState({ ...notifState, frist_erinnerung_minuten: parseInt(e.target.value) || 0 })}
                                    inputProps={{ min: 0, max: 59 }}
                                    sx={{ width: 100 }}
                                    disabled={!notifState.generell_aktiv}
                                />
                                <Typography variant="body2" sx={{ ml: 1 }}>vor Frist-Ablauf</Typography>
                            </Box>

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pl: 2, opacity: notifState.generell_aktiv ? 1 : 0.5 }}>
                                <FormControlLabel control={<Switch checked={notifState.typ_termine} disabled={!notifState.generell_aktiv} onChange={(e) => setNotifState({ ...notifState, typ_termine: e.target.checked })} />} label="Termine & Absagen" />
                                <FormControlLabel control={<Switch checked={notifState.typ_aufstellung} disabled={!notifState.generell_aktiv} onChange={(e) => setNotifState({ ...notifState, typ_aufstellung: e.target.checked })} />} label="Aufstellungen" />
                                <FormControlLabel control={<Switch checked={notifState.typ_kommentar} disabled={!notifState.generell_aktiv} onChange={(e) => setNotifState({ ...notifState, typ_kommentar: e.target.checked })} />} label="Profil Kommentare" />
                                <FormControlLabel control={<Switch checked={notifState.typ_umfrage} disabled={!notifState.generell_aktiv} onChange={(e) => setNotifState({ ...notifState, typ_umfrage: e.target.checked })} />} label="Neue Umfragen" />
                                <FormControlLabel control={<Switch checked={notifState.typ_strafen} disabled={!notifState.generell_aktiv} onChange={(e) => setNotifState({ ...notifState, typ_strafen: e.target.checked })} />} label="Strafenkatalog" />
                                <FormControlLabel control={<Switch checked={notifState.typ_kasse} disabled={!notifState.generell_aktiv} onChange={(e) => setNotifState({ ...notifState, typ_kasse: e.target.checked })} />} label="Kassenbeiträge" />
                            </Box>

                            <Button variant="contained" onClick={handleSaveNotifications} disabled={savingNotif} sx={{ mt: 3 }}>
                                Benachrichtigungen speichern
                            </Button>

                            {notifRes?.success && <Alert severity="success" sx={{ mt: 2 }}>Einstellungen aktualisiert!</Alert>}
                            {notifErr && <Alert severity="error" sx={{ mt: 2 }}>{notifErr}</Alert>}
                        </CardContent>
                    </Card>
                </Grid>

                {/* Cache leeren */}
                <Grid size={{ xs: 12 }}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>Speicher</Typography>
                            <Button variant="outlined" color="error" startIcon={<CachedIcon />} onClick={() => { clearCache(); navigate("/"); }} fullWidth>
                                Cache neuladen
                            </Button>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Logout Button */}
                <Grid size={{ xs: 12 }}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>Account</Typography>
                            <Button variant="outlined" color="error" startIcon={<LogoutIcon />} onClick={handleLogout} fullWidth>
                                Abmelden
                            </Button>
                        </CardContent>
                    </Card>
                </Grid>

            </Grid>
        </Box>
    );
};

export default Settings;
